import { useState, useCallback } from 'react';
import makeRequest, { MakeRequestOptions } from '@justgiving/http-client';
import { captureException, captureMessage, Severity } from '@sentry/browser';

interface FetchResponse<TData> {
  loading: boolean;
  error?: string;
  data?: TData;
}

function usePost<T>(
  url: string,
): [(options: MakeRequestOptions) => void, FetchResponse<T>] {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const post = useCallback(
    (options: MakeRequestOptions) => {
      async function launchRequest() {
        setLoading(true);
        setError(undefined);
        setData(undefined);
        try {
          setData(undefined);
          const response = await makeRequest(url, {
            method: 'POST',
            timeoutInMs: 10000,
            ...options,
          });

          if (response && !response.ok) {
            const errorMessage =
              response.status && response.statusText
                ? `Error: Failed to POST to ${url} - ${response.status} - ${response.statusText}`
                : `Error: Failed to POST to ${url} - Unknown error occurred`;
            setError(errorMessage);
            captureMessage(errorMessage, Severity.Warning);
          } else {
            const responseJson = await response.json();
            setData(responseJson);
          }
        } catch (e) {
          setError(`Error: ${e}`);
          captureException(e);
        } finally {
          setLoading(false);
        }
      }

      launchRequest();
    },
    [url],
  );

  return [post, { data, error, loading }];
}

export default usePost;
